/**
 * 入力内容を URL に載せる。
 *
 * 検定結果は「この数字でこう判断した」という記録として共有されるものなので、
 * リンクを貼れば同じ画面が再現できる状態にしておく。
 */

import type { SequentialSettings } from './stats/analysis'
import type { CorrectionMethod } from './stats/multiple'
import type { SpendingFunction } from './stats/sequential'
import type { Tail } from './stats/types'

export interface SharedState {
  groups: {
    name: string
    visitors: number
    conversions: number
    /** 設計上の割付比。均等割付なら 1。 */
    weight: number
  }[]
  confidence: number
  tail: Tail
  correction: CorrectionMethod
  /** 逐次確認の設定。通常の検定なら null。 */
  sequential: SequentialSettings | null
  /**
   * 逐次確認の指定はあったが読めなかった。
   *
   * 黙って null に落とすと、受け取った人は「厳しい基準で補正されている」と
   * 思ったまま、実際には補正なしの甘い判定を見ることになる。
   * 画面側でこの旨を伝えるためのフラグ。
   */
  sequentialBroken: boolean
}

const TAIL_TO_CODE: Record<Tail, string> = {
  'two-sided': 'two',
  greater: 'gt',
  less: 'lt',
}
const CODE_TO_TAIL: Record<string, Tail> = {
  two: 'two-sided',
  gt: 'greater',
  lt: 'less',
}

const CORRECTION_TO_CODE: Record<CorrectionMethod, string> = {
  none: 'none',
  bonferroni: 'bonf',
  holm: 'holm',
}
const CODE_TO_CORRECTION: Record<string, CorrectionMethod> = {
  none: 'none',
  bonf: 'bonferroni',
  holm: 'holm',
}

const SPENDING_TO_CODE: Record<SpendingFunction, string> = {
  'obrien-fleming': 'of',
  pocock: 'pk',
}
const CODE_TO_SPENDING: Record<string, SpendingFunction> = {
  of: 'obrien-fleming',
  pk: 'pocock',
}

const VALID_CONFIDENCE = [0.9, 0.95, 0.99]

/** 逐次確認の設定として受け付ける確認回数の上限。 */
export const MAX_LOOKS = 20

/** URL に載せるのに必要な部分。復元時にだけ意味を持つ項目は含めない。 */
export type EncodableState = Omit<SharedState, 'sequentialBroken'>

export function encodeState(state: EncodableState): string {
  const params = new URLSearchParams()
  params.set(
    'g',
    state.groups.map((g) => `${g.visitors}-${g.conversions}`).join(','),
  )
  params.set('n', state.groups.map((g) => g.name).join(','))
  // 均等割付は既定なので、不均等のときだけ URL に載せて短く保つ
  if (state.groups.some((g) => g.weight !== 1)) {
    params.set('w', state.groups.map((g) => g.weight).join(','))
  }
  params.set('cl', String(Math.round(state.confidence * 100)))
  params.set('t', TAIL_TO_CODE[state.tail])
  params.set('mc', CORRECTION_TO_CODE[state.correction])
  // 通常の検定が既定なので、途中確認を宣言したときだけ載せる
  if (state.sequential) {
    const { looks, currentLook, spending } = state.sequential
    params.set('sq', `${looks}-${currentLook}-${SPENDING_TO_CODE[spending]}`)
  }
  return params.toString()
}

/**
 * クエリ文字列から状態を復元する。
 * 壊れた値は既定値に落とし、部分的にでも読める分だけ復元する。
 * 復元できる群が 2 つ未満なら null を返して既定の初期状態に任せる。
 */
export function decodeState(search: string): SharedState | null {
  const params = new URLSearchParams(search)
  const raw = params.get('g')
  if (!raw) return null

  const names = (params.get('n') ?? '').split(',')
  const weights = (params.get('w') ?? '').split(',')
  const groups: SharedState['groups'] = []

  raw.split(',').forEach((entry, index) => {
    const [visitorsText, conversionsText] = entry.split('-')
    const visitors = Number(visitorsText)
    const conversions = Number(conversionsText)
    if (
      !Number.isInteger(visitors) ||
      !Number.isInteger(conversions) ||
      visitors < 1 ||
      conversions < 0 ||
      conversions > visitors
    ) {
      return
    }
    const name = names[index]?.trim()
    const weight = Number(weights[index])
    groups.push({
      name: name || defaultGroupName(index),
      visitors,
      conversions,
      // 壊れた割付比は均等に落とす。ここで NaN を通すと SRM が計算不能になる。
      weight: Number.isFinite(weight) && weight > 0 ? weight : 1,
    })
  })

  if (groups.length < 2) return null

  const confidencePercent = Number(params.get('cl'))
  const confidence = VALID_CONFIDENCE.includes(confidencePercent / 100)
    ? confidencePercent / 100
    : 0.95

  return {
    groups,
    confidence,
    tail: CODE_TO_TAIL[params.get('t') ?? ''] ?? 'two-sided',
    correction: CODE_TO_CORRECTION[params.get('mc') ?? ''] ?? 'none',
    sequential: decodeSequential(params.get('sq')),
    sequentialBroken: isSequentialBroken(params.get('sq')),
  }
}

/** 逐次確認の指定があるのに読めなかったか。 */
function isSequentialBroken(raw: string | null): boolean {
  return raw !== null && raw !== '' && decodeSequential(raw) === null
}

/**
 * 逐次確認の設定を復元する。
 * 壊れた値を通常の検定に落とすと「補正されているつもり」で
 * 甘い判定を見せることになるため、読めない場合は null にして
 * 画面側で改めて宣言してもらう。
 */
function decodeSequential(raw: string | null): SequentialSettings | null {
  if (!raw) return null
  const [looksText, currentText, spendingCode] = raw.split('-')
  const looks = Number(looksText)
  const currentLook = Number(currentText)
  const spending = CODE_TO_SPENDING[spendingCode ?? '']
  if (!spending) return null
  if (!Number.isInteger(looks) || looks < 1 || looks > MAX_LOOKS) return null
  if (!Number.isInteger(currentLook) || currentLook < 1 || currentLook > looks) {
    return null
  }
  return { looks, currentLook, spending }
}

/* ============================================================
   連続値の指標（平均の比較）
   ============================================================ */

export interface SharedMeanState {
  groups: { name: string; n: number; mean: number; sd: number }[]
  confidence: number
  tail: Tail
  correction: CorrectionMethod
  sequential: SequentialSettings | null
  sequentialBroken: boolean
}

/**
 * 平均の指標を URL に載せる。
 *
 * 比率と違って値が小数になるため、群ごとの区切りには比率で使っている
 * ハイフンを使えない（負の平均や指数表記と衝突する）。コロンで区切る。
 */
export type EncodableMeanState = Omit<SharedMeanState, 'sequentialBroken'>

export function encodeMeanState(state: EncodableMeanState): string {
  const params = new URLSearchParams()
  params.set('m', state.groups.map((g) => `${g.n}:${g.mean}:${g.sd}`).join(','))
  params.set('n', state.groups.map((g) => g.name).join(','))
  params.set('cl', String(Math.round(state.confidence * 100)))
  params.set('t', TAIL_TO_CODE[state.tail])
  params.set('mc', CORRECTION_TO_CODE[state.correction])
  if (state.sequential) {
    const { looks, currentLook, spending } = state.sequential
    params.set('sq', `${looks}-${currentLook}-${SPENDING_TO_CODE[spending]}`)
  }
  return params.toString()
}

/**
 * 平均の指標の状態を復元する。
 * 比率と同じく、読めない群は捨てて残りで成立させる。
 */
export function decodeMeanState(search: string): SharedMeanState | null {
  const params = new URLSearchParams(search)
  const raw = params.get('m')
  if (!raw) return null

  const names = (params.get('n') ?? '').split(',')
  const groups: SharedMeanState['groups'] = []

  raw.split(',').forEach((entry, index) => {
    const [nText, meanText, sdText] = entry.split(':')
    const n = Number(nText)
    const mean = Number(meanText)
    const sd = Number(sdText)
    if (
      !Number.isInteger(n) ||
      n < 2 ||
      !Number.isFinite(mean) ||
      !Number.isFinite(sd) ||
      sd < 0 ||
      // 空文字は Number では 0 になるので、桁が欠けた指定を弾く
      meanText === undefined ||
      meanText === '' ||
      sdText === undefined ||
      sdText === ''
    ) {
      return
    }
    const name = names[index]?.trim()
    groups.push({ name: name || defaultGroupName(index), n, mean, sd })
  })

  if (groups.length < 2) return null

  const confidencePercent = Number(params.get('cl'))
  return {
    groups,
    confidence: VALID_CONFIDENCE.includes(confidencePercent / 100)
      ? confidencePercent / 100
      : 0.95,
    tail: CODE_TO_TAIL[params.get('t') ?? ''] ?? 'two-sided',
    correction: CODE_TO_CORRECTION[params.get('mc') ?? ''] ?? 'none',
    sequential: decodeSequential(params.get('sq')),
    sequentialBroken: isSequentialBroken(params.get('sq')),
  }
}

/** 群の既定名。A が対照群、以降が比較群。 */
export function defaultGroupName(index: number): string {
  return index === 0 ? 'A (対照)' : `${String.fromCharCode(66 + index - 1)}`
}
